import {useEffect, useState} from "react";
import {faTimes} from "@fortawesome/free-solid-svg-icons";
import Icon from "../../common/elements/Icon";
import Button from "../../common/elements/Button";

const emptyForm = {key: "", description: "", trafficAllocation: 0, control: false};

/**
 * Create/Edit modal for a bucket. Bulma port of thor-ui's GenericCRUDDialog
 * as used by BucketsTab.
 *
 * -- Props --
 * | isActive    | boolean   | Whether the modal is visible
 * | editBucket  | object    | The bucket being edited, or null for create
 * | isSubmitting| boolean   | Disables actions while a request is in flight
 * | onClose     | function  | Called to dismiss the modal
 * | onSubmit    | function  | Called with the form payload on save
 */
export const BucketModal = ({isActive, editBucket, isSubmitting, onClose, onSubmit, otherBucketsTotal = 0}) => {
    const [form, setForm] = useState(emptyForm);

    useEffect(() => {
        if (!isActive) return;
        setForm(editBucket
            ? {
                key: editBucket.key,
                description: editBucket.description ?? "",
                trafficAllocation: editBucket.trafficAllocation,
                control: editBucket.control
            }
            : emptyForm);
    }, [isActive, editBucket]);

    if (!isActive) return null;

    const projectedTotal = otherBucketsTotal + Number(form.trafficAllocation);
    const totalTooHigh = projectedTotal > 101;
    const isValid = !!form.key.trim() && !totalTooHigh;

    return (
        <div className="modal is-active">
            <div className="modal-background" onClick={onClose}></div>
            <div className="modal-card" style={{width: "90%", maxWidth: "520px"}}>
                <header className="modal-card-head">
                    <p className="modal-card-title">{editBucket ? "Edit Bucket" : "Add Bucket"}</p>
                    <button className="delete" aria-label="close" onClick={onClose} disabled={isSubmitting}>
                        <Icon icon={faTimes}/>
                    </button>
                </header>
                <div className="modal-card-body">
                    <div className="field">
                        <label className="label">Key (identifier)</label>
                        <div className="control">
                            <input className="input" type="text" value={form.key}
                                   placeholder="e.g. control"
                                   onChange={(e) => setForm({...form, key: e.target.value})}/>
                        </div>
                        <p className="help">Permanent identifier — cannot be changed after creation. Use a short slug like 'control' or 'treatment_a'.</p>
                    </div>
                    <div className="field">
                        <label className="label">Description</label>
                        <div className="control">
                            <textarea className="textarea" rows={2} value={form.description}
                                      onChange={(e) => setForm({...form, description: e.target.value})}/>
                        </div>
                    </div>
                    <div className="field">
                        <label className="label">Traffic Allocation (%)</label>
                        <div className="control">
                            <input className={`input ${totalTooHigh ? 'is-danger' : ''}`}
                                   type="number" min={0} max={101}
                                   value={form.trafficAllocation}
                                   onChange={(e) => setForm({...form, trafficAllocation: Number(e.target.value)})}/>
                        </div>
                        {totalTooHigh
                            ? <p className="help is-danger">
                                Total would be {projectedTotal}% — cannot exceed 101%. Reduce this value by at least {projectedTotal - 101}%.
                              </p>
                            : <p className="help">New total: {projectedTotal}% (valid range: 96–101%)</p>
                        }
                    </div>
                    <div className="field">
                        <label className="checkbox">
                            <input type="checkbox" checked={form.control}
                                   disabled={!!editBucket}
                                   onChange={(e) => setForm({...form, control: e.target.checked})}/>
                            {" "}Control bucket
                        </label>
                        {editBucket && (
                            <p className="help">Control status cannot be changed after creation.</p>
                        )}
                    </div>
                </div>
                <footer className="modal-card-foot" style={{justifyContent: "flex-end"}}>
                    <Button className="button" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
                    <Button className="button is-link"
                            onClick={() => isValid && onSubmit(form)}
                            disabled={isSubmitting || !isValid}>
                        {isSubmitting ? "Saving..." : (editBucket ? "Update" : "Create")}
                    </Button>
                </footer>
            </div>
        </div>
    );
};

export default BucketModal;
